<!-- FT-220 REQ-01/02/11: настройки контроля повреждений в текущем филиале. -->
<template>
  <div v-if="visible" data-testid="damage-control-settings-card">
    <v-divider class="my-4" />
    <h3 class="mb-2">{{ $t("damage_control.settings.title") }}</h3>
    <v-switch
      v-model="enabled"
      :loading="saving"
      :disabled="saving || formDirty"
      :label="$t('damage_control.settings.switch')"
      hide-details
      data-testid="damage-control-settings-switch"
      @change="save('damage_control_on', $event)"
    />
    <v-switch
      v-model="strict"
      :loading="saving"
      :disabled="saving || formDirty"
      :label="$t('damage_control.settings.strict')"
      hide-details
      data-testid="damage-control-settings-strict"
      @change="save('damage_control_strict', $event)"
    />
    <div class="text-caption mt-1">{{ $t("damage_control.settings.strict_hint") }}</div>
    <div v-if="formDirty" class="text-caption warning--text mt-1">
      {{ $t("damage_control.settings.save_tab_first") }}
    </div>
    <div v-if="trialLeft !== null" class="text-caption mt-2" data-testid="damage-control-settings-trial">
      {{ $t("damage_control.settings.trial", { left: trialLeft, limit: access.trial.limit }) }}
    </div>
    <v-alert v-if="refusal" type="warning" dense outlined class="mt-3" data-testid="damage-control-settings-refusal">
      {{ refusal }}
    </v-alert>
    <v-btn v-if="templatesAvailable" outlined class="mt-3" data-testid="inspection-templates" @click="templatesOpen = true">
      {{ $t('damage_control.templates.title') }}
    </v-btn>
    <templates-dialog v-if="templatesAvailable && templatesOpen" v-model="templatesOpen" />
  </div>
</template>

<script>
import { damageControlApi, damageControlAccess, damageControlEnabled, damageControlInPlan, damageControlTrialLeft, parseDamageControlError } from "@/api/damageControl";
import TemplatesDialog from "@/components/dashboard/damage_control/templates/TemplatesDialog.vue";
import roleMixin from "@/mixins/roleMixin";
import damageControlAccessMixin from "@/mixins/damageControlAccessMixin";

// Неполный успешный ответ тоже оставляет результат неизвестным — перечитываем настройки.
function validSettings(data) {
  const access = data && data.damage_control_access;
  return data && typeof data.damage_control_on === "boolean" && typeof data.damage_control_strict === "boolean" &&
    access && [access.in_plan, access.enabled, access.strict].every((value) => typeof value === "boolean");
}

export default {
  name: "DamageControlSettingsCard",
  components: { TemplatesDialog },
  mixins: [roleMixin, damageControlAccessMixin],
  props: {
    formDirty: { type: Boolean, default: false },
  },
  data() {
    return { enabled: false, strict: false, saving: false, refusal: null, templatesOpen: false };
  },
  computed: {
    company() { return this.damageControlCompany || {}; },
    visible() {
      return this.company.id != null && damageControlInPlan(this.company) && this.isManagerPlus;
    },
    access() {
      return damageControlAccess(this.company);
    },
    templatesAvailable() {
      return this.visible && damageControlEnabled(this.company);
    },
    trialLeft() {
      return damageControlTrialLeft(this.company);
    },
  },
  watch: {
    templatesAvailable(value) { if (!value) this.templatesOpen = false; },
    company: {
      immediate: true,
      handler() {
        if (!this.saving) this.syncSettings();
      },
    },
  },
  methods: {
    syncSettings() {
      this.enabled = this.company.damage_control_on === true;
      this.strict = this.company.damage_control_strict === true;
    },
    applySettings(data, branchId) {
      // Ответ из прежнего филиала не должен изменить настройки нового.
      const current = this.$store.getters.getCompany && this.$store.getters.getCompany.company;
      if (!current || current.id !== branchId) return;
      if (!validSettings(data)) throw new Error("Invalid damage control settings response");
      // Только поля модуля поверх живого стора: профиль и параллельные рассылки сохраняются.
      this.$store.commit("setCompany", { company: {
        ...current,
        damage_control_on: data.damage_control_on,
        damage_control_strict: data.damage_control_strict,
        damage_control_access: data.damage_control_access,
      } });
    },
    async save(field, value) {
      if (this.saving || this.formDirty || !this.visible) return;
      const branchId = this.company.id;
      this.saving = true;
      this.refusal = null;
      try {
        const data = await damageControlApi.updateSettings(this.axios, { [field]: value });
        this.applySettings(data, branchId);
      } catch (err) {
        if (this.company.id !== branchId) return;
        const parsed = parseDamageControlError(err);
        if (parsed) {
          this.refusal = this.$t(parsed.i18nKey);
        } else {
          // Сеть, неизвестный код или битый ответ: PATCH мог сохраниться, читаем значение сервера.
          try {
            this.applySettings(await damageControlApi.getSettings(this.axios), branchId);
          } catch (_reloadError) {
            // Нет достоверного ответа — остаётся последнее значение стора.
          }
          if (this.company.id === branchId) this.refusal = this.$t("damage_control.settings.unknown_result");
        }
      } finally {
        this.saving = false;
        this.syncSettings();
      }
    },
  },
};
</script>
